import { ReactElement, memo } from 'react'

import { Modal, ModalProps, styled } from '@mui/material'

import { PanelAnimation, FoldoutPanelProps, FoldoutContext } from '@shared/components/Foldout'

type PlacementOptions = 'start' | 'end'

const Wrapper = styled(Modal, { shouldForwardProp: (prop) => prop !== 'placement' })<{ placement: PlacementOptions }>(
  ({ placement }) => ({
    width: '100vw',
    display: 'flex',
    alignItems: 'stretch',
    justifyContent: `flex-${placement}`,
  }),
)

export type FoldoutProps = {
  placement: PlacementOptions
  /**
   * Children for Foldout component.
   * @remarks Only use `FoldoutPanel` component as value. DO NOT USE ANY OTHER COMPONENT!
   * @example
   * ```tsx
   * <Foldout
   *   {...foldoutProps}
   * >
   *  <FoldoutPanel {...foldoutPanelProps}>{...}</FoldoutPanel>
   * </Foldout>
   * ```
   */
  children: ReactElement<FoldoutPanelProps>
} & Omit<ModalProps<'div'>, 'component' | 'title' | 'onClose'> &
  Required<Pick<ModalProps, 'onClose'>>

const RootRaw = ({ placement, ...modalProps }: FoldoutProps) => {
  return (
    <Wrapper hideBackdrop {...modalProps} placement={placement} closeAfterTransition>
      <PanelAnimation in={modalProps.open}>
        <FoldoutContext.Provider value={{ onClose: modalProps.onClose }}>{modalProps.children}</FoldoutContext.Provider>
      </PanelAnimation>
    </Wrapper>
  )
}

export const Foldout = memo(RootRaw)
